
import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";

const TermsAndConditionFreelanceContract = () => {
  return (
    <div className="container mx-auto py-12 px-4 sm:px-6 lg:px-8 max-w-5xl">
      <div className="mb-8 text-center">
        <h1 className="text-4xl font-bold bg-gradient-to-r from-purple-600 via-blue-500 to-cyan-400 bg-clip-text text-transparent mb-4">
          Terms of Service
        </h1>
        <p className="text-gray-500 text-lg max-w-3xl mx-auto">
          Last updated: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
        </p>
        <Separator className="my-8" />
      </div>

      <Card className="mb-8 shadow-lg border-0 bg-white/50 backdrop-blur-sm">
        <CardHeader>
          <CardTitle className="text-2xl font-bold text-gray-800">1. Agreement to Terms</CardTitle>
        </CardHeader>
        <CardContent className="text-gray-600 space-y-4">
          <p>
            By accessing or using the DGK Freelance Hub platform, you agree to be bound by these Terms of Service and all applicable laws and regulations. If you do not agree with any of these terms, you are prohibited from using or accessing this platform.
          </p>
          <p>
            The materials contained in this platform are protected by applicable copyright and trademark law.
          </p>
        </CardContent>
      </Card>

      <Card className="mb-8 shadow-lg border-0 bg-white/50 backdrop-blur-sm">
        <CardHeader>
          <CardTitle className="text-2xl font-bold text-gray-800">2. Use License</CardTitle>
        </CardHeader>
        <CardContent className="text-gray-600 space-y-4">
          <p>
            Permission is granted to temporarily access the materials on DGK Freelance Hub's platform for personal, non-commercial transitory viewing only. This is the grant of a license, not a transfer of title, and under this license you may not:
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li>Modify or copy the materials;</li>
            <li>Use the materials for any commercial purpose or for any public display;</li>
            <li>Attempt to reverse engineer any software contained on DGK Freelance Hub's platform;</li>
            <li>Remove any copyright or other proprietary notations from the materials; or</li>
            <li>Transfer the materials to another person or "mirror" the materials on any other server.</li>
          </ul>
          <p className="mt-4">
            This license shall automatically terminate if you violate any of these restrictions and may be terminated by DGK Freelance Hub at any time. Upon terminating your viewing of these materials or upon the termination of this license, you must destroy any downloaded materials in your possession whether in electronic or printed format.
          </p>
        </CardContent>
      </Card>

      <Card className="mb-8 shadow-lg border-0 bg-white/50 backdrop-blur-sm">
        <CardHeader>
          <CardTitle className="text-2xl font-bold text-gray-800">3. Disclaimer</CardTitle>
        </CardHeader>
        <CardContent className="text-gray-600 space-y-4">
          <p>
            The materials on DGK Freelance Hub's platform are provided on an 'as is' basis. DGK Freelance Hub makes no warranties, expressed or implied, and hereby disclaims and negates all other warranties including, without limitation, implied warranties or conditions of merchantability, fitness for a particular purpose, or non-infringement of intellectual property or other violation of rights.
          </p>
          <p>
            Further, DGK Freelance Hub does not warrant or make any representations concerning the accuracy, likely results, or reliability of the use of the materials on its platform or otherwise relating to such materials or on any sites linked to this platform.
          </p>
        </CardContent>
      </Card>

      <Card className="mb-8 shadow-lg border-0 bg-white/50 backdrop-blur-sm">
        <CardHeader>
          <CardTitle className="text-2xl font-bold text-gray-800">4. Limitations</CardTitle>
        </CardHeader>
        <CardContent className="text-gray-600 space-y-4">
          <p>
            In no event shall DGK Freelance Hub or its suppliers be liable for any damages (including, without limitation, damages for loss of data or profit, or due to business interruption) arising out of the use or inability to use the materials on DGK Freelance Hub's platform, even if DGK Freelance Hub or a DGK Freelance Hub authorized representative has been notified orally or in writing of the possibility of such damage.
          </p>
          <p>
            Because some jurisdictions do not allow limitations on implied warranties, or limitations of liability for consequential or incidental damages, these limitations may not apply to you.
          </p>
        </CardContent>
      </Card>

      <Card className="mb-8 shadow-lg border-0 bg-white/50 backdrop-blur-sm">
        <CardHeader>
          <CardTitle className="text-2xl font-bold text-gray-800">5. Accuracy of Materials</CardTitle>
        </CardHeader>
        <CardContent className="text-gray-600 space-y-4">
          <p>
            The materials appearing on DGK Freelance Hub's platform could include technical, typographical, or photographic errors. DGK Freelance Hub does not warrant that any of the materials on its platform are accurate, complete, or current. DGK Freelance Hub may make changes to the materials contained on its platform at any time without notice. However, DGK Freelance Hub does not make any commitment to update the materials.
          </p>
        </CardContent>
      </Card>

      <Card className="mb-8 shadow-lg border-0 bg-white/50 backdrop-blur-sm">
        <CardHeader>
          <CardTitle className="text-2xl font-bold text-gray-800">6. Payment and Fees</CardTitle>
        </CardHeader>
        <CardContent className="text-gray-600 space-y-4">
          <p>
            DGK Freelance Hub charges fees for certain services. When you use a paid service, you agree to pay the fees and applicable taxes. Failure to pay these fees may result in the termination of your service.
          </p>
          <p>
            For projects facilitated through the platform, DGK Freelance Hub will hold payments in escrow until project milestones are met and approved by the client. A service fee will be deducted from payments processed through the platform.
          </p>
          <p>
            Cryptocurrency payments are subject to network transaction fees which are separate from DGK Freelance Hub service fees.
          </p>
        </CardContent>
      </Card>

      <Card className="mb-8 shadow-lg border-0 bg-white/50 backdrop-blur-sm">
        <CardHeader>
          <CardTitle className="text-2xl font-bold text-gray-800">7. Dispute Resolution</CardTitle>
        </CardHeader>
        <CardContent className="text-gray-600 space-y-4">
          <p>
            Any disputes arising from or related to these Terms of Service or the use of DGK Freelance Hub's platform shall first be attempted to be resolved through our dispute resolution process. If the dispute cannot be resolved through our internal process, the parties agree to submit to binding arbitration.
          </p>
          <p>
            For contract disputes between freelancers and clients, DGK Freelance Hub provides a mediation service that both parties can use before resorting to formal arbitration.
          </p>
        </CardContent>
      </Card>

      <Card className="mb-8 shadow-lg border-0 bg-white/50 backdrop-blur-sm">
        <CardHeader>
          <CardTitle className="text-2xl font-bold text-gray-800">8. Modifications</CardTitle>
        </CardHeader>
        <CardContent className="text-gray-600 space-y-4">
          <p>
            DGK Freelance Hub may revise these Terms of Service for its platform at any time without notice. By using this platform, you are agreeing to be bound by the then current version of these Terms of Service.
          </p>
        </CardContent>
      </Card>

      <Card className="mb-8 shadow-lg border-0 bg-white/50 backdrop-blur-sm">
        <CardHeader>
          <CardTitle className="text-2xl font-bold text-gray-800">9. Governing Law</CardTitle>
        </CardHeader>
        <CardContent className="text-gray-600 space-y-4">
          <p>
            These terms and conditions are governed by and construed in accordance with the laws and you irrevocably submit to the exclusive jurisdiction of the courts in that location.
          </p>
        </CardContent>
      </Card>

      <Card className="mb-8 shadow-lg border-0 bg-white/50 backdrop-blur-sm">
        <CardHeader>
          <CardTitle className="text-2xl font-bold text-gray-800">Contact Us</CardTitle>
        </CardHeader>
        <CardContent className="text-gray-600 space-y-4">
          <p>
            If you have any questions about these Terms of Service, please contact us at:
          </p>
          <p className="p-4 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg border border-blue-100 mt-2">
            <strong>Email:</strong> legal@dgkfreelancehub.com<br />
            <strong>Address:</strong> 123 Blockchain Boulevard, Crypto City, CC 12345<br />
            <strong>Phone:</strong> +1 (555) 123-4567
          </p>
        </CardContent>
      </Card>

      <div className="text-center mt-12 text-sm text-gray-500">
        <p>© {new Date().getFullYear()} DGK Freelance Hub. All rights reserved.</p>
      </div>
    </div>
  );
};

export default TermsAndConditionFreelanceContract;
